import { Stack } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { useTranslation } from '../../../hooks/useTranslation';
import { Agent, AgentCapability } from '../../../interfaces/Agent';
import StyledFormItem from '../../../styles/StyledFormItem';
import Button from '../../shared/Button/Button';
import Dropdown from '../../shared/DropDown/DropDown';
import Text from '../../shared/Text/Text';

type CapabilityOption = {
  label: string;
  value: AgentCapability;
  disabled?: boolean;
};

interface ChooseCapabilityProps {
  capabilities: CapabilityOption[];
  agent?: Agent;
  onToggleEmployeeDirectory: () => void;
}

const ChooseCapabilityV1: React.FC<ChooseCapabilityProps> = ({
  capabilities,
  agent,
  onToggleEmployeeDirectory,
}) => {
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext();
  const { track } = useAnalytics();

  // Watch the current capability arrays to determine which are already in use
  const transferCalls = watch('transfer_calls') || [];
  const bookAppointments = watch('insert_appointment') || [];
  const selectedCapability = watch('external_capabilities') || '';

  // Initialize form fields when editing an existing agent
  useEffect(() => {
    if (agent) {
      const transferCalls = agent.external_capabilities?.transfer_calls;
      if (transferCalls === 'employee_directory') {
        setValue('transfer_calls', 'employee_directory');
      } else if (Array.isArray(transferCalls) && transferCalls.length > 0) {
        setValue('transfer_calls', transferCalls);
        setValue('external_capabilities', 'transfer_calls');
      }
      if (
        agent.external_capabilities?.insert_appointment &&
        agent.external_capabilities.insert_appointment.length > 0
      ) {
        setValue(
          'insert_appointment',
          agent.external_capabilities.insert_appointment
        );
        setValue('external_capabilities', 'insert_appointment');
      }
    }
  }, [agent, setValue]);

  // Check if capabilities are already in use
  // Only disable if the capability is already configured in the CURRENT form state
  // AND it's not the currently selected capability
  const isEmployeeDirectory = transferCalls === 'employee_directory';
  const isTransferCallsInUse =
    (Array.isArray(transferCalls) &&
      transferCalls.length > 0 &&
      selectedCapability !== 'transfer_calls') ||
    isEmployeeDirectory;
  const isBookAppointmentsInUse =
    bookAppointments.length > 0 && selectedCapability !== 'insert_appointment';

  // Create capability options with disabled state based on current usage
  const availableCapabilities = capabilities.map(capability => ({
    ...capability,
    disabled:
      capability.value === AgentCapability.TRANSFER_CALLS
        ? isTransferCallsInUse
        : capability.value === AgentCapability.INSERT_APPOINTMENT
          ? isBookAppointmentsInUse
          : false,
  }));

  // Check if all capabilities are disabled
  const allCapabilitiesDisabled = availableCapabilities.every(
    cap => cap.disabled
  );

  const handleCapabilityChange = useCallback(
    (value: string) => {
      setValue('external_capabilities', value);
      if (value) {
        track(AnalyticsEvents.AGENT_CAPABILITY_CHOSEN, { capability: value });
      }
    },
    [setValue, track]
  );

  const handleAddCapability = useCallback(() => {
    if (!selectedCapability) return;

    if (selectedCapability === AgentCapability.TRANSFER_CALLS) {
      // Add a new transfer call entry
      const currentTransferCalls = watch('transfer_calls') || [];
      setValue('transfer_calls', [
        ...currentTransferCalls,
        { department_name: '', phone_number: '+352' },
      ]);
    } else if (selectedCapability === AgentCapability.INSERT_APPOINTMENT) {
      // Add a new book appointment entry
      const currentBookAppointments = watch('insert_appointment') || [];
      setValue('insert_appointment', [
        ...currentBookAppointments,
        {
          start_time: '',
          end_time: '',
          min_advance_notice_hours: '',
          appointment_length_minutes: '',
          app: '',
        },
      ]);
    }

    setValue('external_capabilities', '');
  }, [selectedCapability, setValue, watch]);

  useEffect(() => {
    setValue('external_capabilities', '');
  }, []);

  return (
    <>
      <StyledFormItem flexDirection="row" gap="1rem">
        <Stack direction="column" flexGrow={1} alignItems="flex-start">
          <Text type="label">{t('Choose a capability')}</Text>
          <Dropdown
            name="external_capabilities"
            options={availableCapabilities}
            fullWidth
            onChange={event => handleCapabilityChange(event.target.value)}
          />
        </Stack>
        <Stack
          direction="column"
          gap="1rem"
          justifyContent="flex-end"
          paddingBottom="3px"
        >
          <Button
            size="large"
            onClick={handleAddCapability}
            disabled={!selectedCapability || allCapabilitiesDisabled}
          >
            {t('Add Capability')}
          </Button>
        </Stack>
      </StyledFormItem>
      {allCapabilitiesDisabled && (
        <Text
          type="body2"
          style={{
            color: 'var(--mui-palette-text-secondary)',
            fontSize: '12px',
            marginTop: '4px',
          }}
        >
          {t(
            'All capabilities are already configured. Remove existing configurations to add new ones.'
          )}
        </Text>
      )}
    </>
  );
};

export default ChooseCapabilityV1;
